import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MultiSelect } from 'primereact/multiselect';
import useIlceMahalleSecimi from '../hooks/useIlceMahalleSecimi';
import { sendNotificationIlceMahalle, sendNotificationTumVatandas } from '../services/notificationService';

const TIP_OPTIONS = [
  { label: 'Tümü', value: 'Tümü' },
  { label: 'Mahalle Seç', value: 'Mahalle' },
];

const YeniDuyuruModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    tip: 'Tümü',
    mesaj: '',
    image: null,
  });
  const [loading, setLoading] = useState({
    submitting: false,
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  // Çoklu ilçe + ilçeye göre gruplanmış çoklu mahalle; tip Mahalle değilken ya da modal kapanınca seçim sıfırlanır.
  const secim = useIlceMahalleSecimi({ aktif: isOpen && formData.tip === 'Mahalle' });

  // Disable body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        tip: 'Tümü',
        mesaj: '',
        image: null,
      });
      setErrors({});
      setImagePreview(null);
    }
  }, [isOpen]);

  const handleTipChange = (e) => {
    setFormData(prev => ({ ...prev, tip: e.target.value }));
    setErrors(prev => ({ ...prev, ilce: null, mahalleler: null }));
  };

  const handleIlceChange = (e) => {
    secim.ilceDegistir(e.value);
    setErrors(prev => ({ ...prev, ilce: null, mahalleler: null }));
  };

  const handleMahalleChange = (e) => {
    secim.mahalleDegistir(e.value);
    setErrors(prev => ({ ...prev, mahalleler: null }));
  };

  const handleMesajChange = (e) => {
    setFormData(prev => ({ ...prev, mesaj: e.target.value }));
    setErrors(prev => ({ ...prev, mesaj: null }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({ ...prev, image: null }));
      setImagePreview(null);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }));
    setImagePreview(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.mesaj.trim()) {
      newErrors.mesaj = 'Mesaj alanı zorunludur';
    }

    if (formData.tip === 'Mahalle') {
      if (secim.ilceler.length === 0) {
        newErrors.ilce = 'En az bir ilçe seçmelisiniz';
      } else if (secim.mahalleYukleniyor) {
        newErrors.mahalleler = 'Mahalleler yükleniyor, lütfen bekleyiniz';
      } else {
        // Mahallesi seçilmemiş ilçe "tüm ilçe" sanılmasın diye her seçili ilçede en az bir mahalle istenir
        const eksikId = secim.ilceler.find(id => !secim.mahalleler.some(a => a.startsWith(`${id}|`)));
        if (secim.mahalleler.length === 0) {
          newErrors.mahalleler = 'En az bir mahalle seçmelisiniz';
        } else if (eksikId) {
          const eksikAd = secim.ilceSecenekleri.find(d => d.id === eksikId)?.name ?? eksikId;
          newErrors.mahalleler = `${eksikAd} ilçesi için en az bir mahalle seçiniz veya ilçeyi kaldırınız`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(prev => ({ ...prev, submitting: true }));

    try {
      // Prepare form data for submission
      const submitData = new FormData();
      submitData.append('Mesaj', formData.mesaj.trim());
      if (formData.image) {
        submitData.append('image', formData.image);
      }

      let response;
      if (formData.tip === 'Mahalle') {
        // Mahalle adı ilçeler arasında tekil değil (YALI: GÜZELBAHÇE/KARŞIYAKA); her seçim "İLÇE|MAHALLE"
        // çifti olarak ayrı alanda gider, API ilçe + mahalle birlikte süzer.
        secim.secilenCiftler().forEach(({ ilce, mahalle }) => submitData.append('IlceMahalleler', `${ilce}|${mahalle}`));
        response = await sendNotificationIlceMahalle(submitData);
      } else {
        // Adresi olan herkese; birden çok adresi olan vatandaşa tek bildirim gider
        response = await sendNotificationTumVatandas(submitData);
      }

      if (response?.status !== 200) {
        setErrors(prev => ({ ...prev, submit: response?.message || 'Duyuru gönderilirken bir hata oluştu' }));
        return;
      }
      alert(response.message);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Failed to send notification:', error);
      setErrors(prev => ({ ...prev, submit: 'Duyuru gönderilirken bir hata oluştu' }));
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevent scroll from propagating to the page behind
  const handleModalWheel = (e) => {
    const modalContent = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = modalContent;
    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight;
    
    // If scrolling up at top or scrolling down at bottom, prevent propagation
    if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
      e.preventDefault();
    }
    e.stopPropagation();
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick} onWheel={(e) => e.stopPropagation()}>
      <div className="modal-content yeni-duyuru-modal" onClick={e => e.stopPropagation()} onWheel={handleModalWheel}>
        <div className="modal-header">
          <h3>Yeni Bildirim</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="duyuru-form">
          {/* Tip Selection */}
          <div className="form-group">
            <label>Tip *</label>
            <select
              value={formData.tip}
              onChange={handleTipChange}
              className="form-input"
            >
              {TIP_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* İlçe MultiSelect - Only shown if Mahalle is selected */}
          {formData.tip === 'Mahalle' && (
            <div className="form-group mahalle-group">
              <label>İlçe *</label>
              <MultiSelect
                value={secim.ilceler}
                options={secim.ilceSecenekleri}
                onChange={handleIlceChange}
                optionLabel="name"
                optionValue="id"
                placeholder={secim.ilceYukleniyor ? 'Yükleniyor...' : 'İlçe Seçiniz'}
                className={`mahalle-multiselect ${errors.ilce ? 'p-invalid' : ''}`}
                disabled={secim.ilceYukleniyor || secim.ilceSecenekleri.length === 0}
                filter
                display="chip"
                maxSelectedLabels={3}
                selectedItemsLabel="{0} ilçe seçildi"
                appendTo="self"
                panelClassName="mahalle-panel"
              />
              {errors.ilce && <span className="error-text">{errors.ilce}</span>}
            </div>
          )}

          {/* Mahalle MultiSelect (seçili ilçelere göre gruplu) - Only shown if Mahalle is selected */}
          {formData.tip === 'Mahalle' && (
            <div className="form-group mahalle-group">
              <label>Mahalle *</label>
              <MultiSelect
                value={secim.mahalleler}
                options={secim.mahalleGruplari}
                onChange={handleMahalleChange}
                optionGroupLabel="ilceAdi"
                optionGroupChildren="items"
                optionLabel="etiket"
                optionValue="anahtar"
                itemTemplate={(secenek) => secenek.ad}
                filterBy="ad"
                placeholder={secim.mahalleYukleniyor ? 'Yükleniyor...' : 'Mahalle Seçiniz'}
                className={`mahalle-multiselect ${errors.mahalleler ? 'p-invalid' : ''}`}
                disabled={!secim.ilceler.length || secim.mahalleGruplari.length === 0}
                filter
                display="chip"
                maxSelectedLabels={3}
                selectedItemsLabel="{0} mahalle seçildi"
                appendTo="self"
                panelClassName="mahalle-panel"
              />
              {errors.mahalleler && <span className="error-text">{errors.mahalleler}</span>}
              {secim.mahalleler.length > 0 && (
                <div className="selected-count">
                  {secim.ilceler.length > 1
                    ? `${secim.ilceler.length} ilçede ${secim.mahalleler.length} mahalle seçildi`
                    : `${secim.mahalleler.length} mahalle seçildi`}
                </div>
              )}
            </div>
          )}

          {/* Mesaj */}
          <div className="form-group">
            <label>Mesaj *</label>
            <textarea
              value={formData.mesaj}
              onChange={handleMesajChange}
              className={`form-input textarea ${errors.mesaj ? 'error' : ''}`}
              placeholder="Bildirim mesajınızı yazın..."
              rows={5}
            />
            {errors.mesaj && <span className="error-text">{errors.mesaj}</span>}
          </div>

          {/* Image Upload */}
          <div className="form-group">
            <label>Görsel (Opsiyonel)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="form-input file-input"
            />
            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Preview" />
                <button type="button" className="remove-image" onClick={removeImage}>
                  Görseli Kaldır
                </button>
              </div>
            )}
          </div>

          {/* Error message */}
          {errors.submit && (
            <div className="form-error">{errors.submit}</div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={loading.submitting}
            >
              İptal
            </button>
            <button
              type="submit"
              className="duyuru-submit-button submit-button"
              disabled={loading.submitting}
            >
              {loading.submitting ? 'Gönderiliyor...' : 'Bildirim Gönder'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          overflow: hidden;
        }

        .yeni-duyuru-modal {
          background: white;
          padding: 24px;
          border-radius: 12px;
          width: 550px;
          max-width: 95%;
          max-height: 90vh;
          overflow-y: auto;
          overscroll-behavior: contain;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid #eee;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #18964e;
        }

        .close-button {
          background: #18964e;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: white;
          line-height: 1;
          padding: 8px 16px;
          border-radius: 8px;
        }

        .close-button:hover {
          background: #147a3f;
        }

        .duyuru-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-weight: 600;
          color: #333;
          font-size: 14px;
        }

        .form-input {
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s;
          background-color: white;
        }

        .form-input:focus {
          outline: none;
          border-color: #18964e;
        }

        .form-input.error {
          border-color: #dc3545;
        }

        .form-input.textarea {
          resize: vertical;
          min-height: 120px;
          font-family: inherit;
        }

        .file-input {
          padding: 10px;
        }

        .error-text {
          color: #dc3545;
          font-size: 12px;
        }

        .selected-count {
          font-size: 12px;
          color: #18964e;
          font-weight: 500;
        }

        .image-preview {
          margin-top: 12px;
          text-align: center;
        }

        .image-preview img {
          max-width: 100%;
          max-height: 200px;
          border-radius: 8px;
          border: 1px solid #ddd;
        }

        .remove-image {
          display: block;
          margin: 10px auto 0;
          padding: 6px 12px;
          background-color: #dc3545;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }

        .remove-image:hover {
          background-color: #c82333;
        }

        .form-error {
          padding: 12px;
          background-color: #f8d7da;
          color: #721c24;
          border-radius: 8px;
          font-size: 14px;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 8px;
          padding-top: 16px;
          border-top: 1px solid #eee;
        }

        .cancel-button {
          padding: 12px 24px;
          background-color: #f5f5f5;
          color: #333;
          border: 1px solid #ddd;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .cancel-button:hover:not(:disabled) {
          background-color: #e9e9e9;
        }

        .cancel-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .submit-button {
          padding: 12px 24px;
          background-color: #18964e;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .submit-button:hover:not(:disabled) {
          background-color: #147a3f;
        }

        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .mahalle-group {
          position: relative;
        }
      `}</style>
    </div>
  );
};

YeniDuyuruModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default YeniDuyuruModal;
